import { onAuthStateChanged } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  getFirebaseApp,
  getFirebaseAuth,
  getFirebaseDb,
} from "@/lib/firebase";
import type { User } from "firebase/auth";

// Getters: solo cliente; en SSR devuelven null (no inicializar Firebase en build).
export function getAuth() {
  return getFirebaseAuth();
}
export function getDb() {
  return getFirebaseDb();
}
export function getApp() {
  return getFirebaseApp();
}

const AUTH_WAIT_MS = 15_000;

/**
 * Espera a que auth tenga un usuario (restauración de sesión).
 * Resuelve con el User o con null si no hay sesión tras la primera emisión de onAuthStateChanged.
 */
export function waitForAuth(): Promise<User | null> {
  return new Promise((resolve, reject) => {
    const auth = getFirebaseAuth();
    if (!auth) {
      reject(new Error("Firebase not available"));
      return;
    }
    const timeout = setTimeout(() => {
      unsub();
      reject(new Error("Auth timeout"));
    }, AUTH_WAIT_MS);
    const unsub = onAuthStateChanged(auth, (user) => {
      clearTimeout(timeout);
      unsub();
      resolve(user);
    });
  });
}

/**
 * Llama una Cloud Function (onCall) solo cuando hay usuario, para que el SDK envíe el token.
 * Evita 401 "Debe estar autenticado" por llamar antes de que auth.currentUser esté listo.
 */
export async function callAuthedFunction<T = unknown>(
  name: string,
  payload: Record<string, unknown> = {}
): Promise<T> {
  const user = await waitForAuth();
  if (!user) {
    throw new Error("Debe estar autenticado para acceder a esta función.");
  }
  const app = getFirebaseApp();
  if (!app) throw new Error("Firebase not available");
  const functions = getFunctions(app, "southamerica-east1");
  const callable = httpsCallable(functions, name);
  const result = await callable(payload);
  return result.data as T;
}

export async function fetchDashboardData() {
  return callAuthedFunction<Record<string, unknown>>("getDashboardData", {
    panelVersion: "v0",
  });
}

export type ExchangeRateData = {
  usdArsSell: number;  // valor vendedor BNA (el que usamos)
  usdArsBuy: number;   // valor comprador (solo referencia)
  date: string;        // "YYYY-MM-DD"
  source: string;
  apiUpdatedAt: string | null;
} | null;

/**
 * Obtiene el tipo de cambio USD/ARS actual desde la Cloud Function.
 * Fuente: BNA valor vendedor, actualizado automáticamente cada día hábil.
 * Devuelve null si aún no hay dato (primera ejecución pendiente).
 */
export async function fetchExchangeRate(): Promise<ExchangeRateData> {
  return callAuthedFunction<ExchangeRateData>("getExchangeRate", {});
}

/**
 * Helper: convierte un monto en ARS a USD usando el valor vendedor BNA.
 * Retorna null si no hay tipo de cambio disponible.
 */
export function arsToUsd(amountArs: number, rate: ExchangeRateData): number | null {
  if (!rate || !rate.usdArsSell || rate.usdArsSell <= 0) return null;
  return amountArs / rate.usdArsSell;
}

export type RegisterPaymentResult = {
  paymentId: string;
  appliedAmount: number;
  newAmountPaid: number;
  newAmountPending: number;
  newPaymentStatus: "paid" | "partial" | "pending";
};

/**
 * Registra un pago parcial o total sobre una venta con deuda.
 * Llama a la Cloud Function registerPayment que actualiza la venta en una transacción.
 */
export async function registerPayment(params: {
  breweryId: string;
  saleId: string;
  amount: number;
  paymentMethod: string;
  notes?: string;
}): Promise<RegisterPaymentResult> {
  return callAuthedFunction<RegisterPaymentResult>("registerPayment", {
    breweryId: params.breweryId,
    saleId: params.saleId,
    amount: params.amount,
    paymentMethod: params.paymentMethod,
    notes: params.notes ?? "",
  });
}

export type CreateSaleItemInput = {
  itemId: string;
  quantity: number;
  unitPrice: number;
  unit?: string | null;
};

export type CreateSaleResult = { saleId: string; saleNumber: number; totalAmount: number };

/**
 * Crea una venta vía la Cloud Function `createSale` (G-0002b): el servidor
 * recalcula los importes desde los ítems, valida los productos del tenant y
 * estampa la autoría. El cliente ya NO escribe la venta en Firestore
 * (firestore.rules lo deniega). El resto del flujo (cobro, barriles) sigue
 * orquestándose desde el cliente con el saleId devuelto.
 */
export async function createSale(params: {
  breweryId: string;
  items: CreateSaleItemInput[];
  paymentMethod: string;
  priceTier: string;
  priceTierLabel?: string | null;
  discountAmount?: number;
  clientId?: string | null;
  clientName?: string | null;
  seller?: { id: string; name: string; commissionPct?: number };
  kegIds?: string[];
  feria?: { sessionId: string; name: string };
}): Promise<CreateSaleResult> {
  return callAuthedFunction<CreateSaleResult>("createSale", {
    breweryId: params.breweryId,
    items: params.items.map((i) => ({
      itemId: i.itemId,
      quantity: i.quantity,
      unitPrice: i.unitPrice,
      unit: i.unit ?? null,
    })),
    paymentMethod: params.paymentMethod,
    priceTier: params.priceTier,
    priceTierLabel: params.priceTierLabel ?? null,
    discountAmount: params.discountAmount ?? 0,
    clientId: params.clientId ?? null,
    clientName: params.clientName ?? null,
    seller: params.seller ?? null,
    kegIds: params.kegIds ?? [],
    feria: params.feria ?? null,
  });
}

/**
 * Formatea un monto en ARS con su equivalente USD si hay tipo de cambio.
 * Ejemplo: "$50.000 ≈ USD 44"
 */
export function formatArsWithUsd(amountArs: number, rate: ExchangeRateData): string {
  const base = `$${amountArs.toLocaleString("es-AR")}`;
  const usd = arsToUsd(amountArs, rate);
  if (usd === null) return base;
  return `${base} ≈ USD ${Math.round(usd).toLocaleString("es-AR")}`;
}
